
import React, {Component} from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  TextInput,
  Animated,
  Easing
} from 'react-native';
import modalStyles from '../../style/boxChampCalculateModal';
import {noticeStyles, noticeTones} from './boxChampNoticeStyles';
import {HANDOFF_IDLE, HANDOFF_AWAITING_FIRST_VERIFY} from './boxChampHandoff';

// GROOV-4994 (hand-off timer): confirmed with Dan — a 4s auto-confirm countdown runs
// whenever the "Next Box"/"Complete Order"/"Start packing" button is on screen (i.e.
// whenever a box scan is NOT required), and firing it must act EXACTLY like a click of
// that same button (same onConfirm prop → confirmBoxChampHandoff, which is itself
// idempotent and re-reads live enforcement state — see scanpack_item.js). No timer runs
// at all when requiresBoxScan is true; in that state the button doesn't render either.
export const HANDOFF_TIMER_DURATION_MS = 4000;

const tone = {
  white: '#ffffff',
  body: '#E0E7EF',
  muted: '#9aafc0',
  rule: '#3d5a73',
  hero: '#253c57',
  accent: '#5f9bd8',
  warn: '#FFC46B',
  warnBg: 'rgba(255,196,107,0.10)',
};

const label = {
  fontFamily: 'Poppins_500Medium',
  fontSize: 11,
  letterSpacing: 1.1,
  color: tone.muted,
};

const thumb = size => ({
  width: size,
  height: size,
  borderRadius: 4,
  backgroundColor: tone.white,
  marginRight: 10,
});

export default class BoxChampHandoffCard extends Component {
  state = {input: '', timerPaused: false};
  inputRef = React.createRef();
  _fillAnim = new Animated.Value(1);
  _remainingMs = HANDOFF_TIMER_DURATION_MS;
  _running = false;
  _timerKey = null;

  focusInput = () => {
    clearTimeout(this._focusTimer);
    this._focusTimer = setTimeout(() => {
      if (this.inputRef.current && this.inputRef.current.focus) this.inputRef.current.focus();
    }, 150);
  };

  cardVisible = props => {
    const kind = props.state && props.state.kind;
    return Boolean(kind && kind !== HANDOFF_IDLE && !props.dismissed && props.state.requiresBoxScan && props.onScan);
  };

  // The button (and therefore the countdown) exists exactly when a box scan is NOT
  // required — mirrors the `!requiresScan` gate around the button in render().
  timerEligible = props => {
    const kind = props.state && props.state.kind;
    return Boolean(kind && kind !== HANDOFF_IDLE && !props.state.requiresBoxScan && props.onConfirm);
  };

  // Same identity key componentDidUpdate already uses for scan-input focus — a new
  // pending box (or first-verify target) means a fresh 4s window, not a continuation.
  timerKey = props => {
    const st = props.state;
    if (!st || !st.kind || st.kind === HANDOFF_IDLE) return null;
    return `${st.kind}:${st.sealBox?.id ?? ''}:${st.expectedBox?.id ?? ''}:${st.isLastBox ? 1 : 0}`;
  };

  shouldTimerRun = (props, state) =>
    this.timerEligible(props) && !props.dismissed && !state.timerPaused;

  beginTimerRun = () => {
    const duration = this._remainingMs;
    if (duration <= 0) {
      this.handleTimerExpire();
      return;
    }
    this._running = true;
    this._runStartedAt = Date.now();
    this._animation = Animated.timing(this._fillAnim, {
      toValue: 0,
      duration,
      easing: Easing.linear,
      useNativeDriver: false,
    });
    this._animation.start();
    this._confirmTimer = setTimeout(this.handleTimerExpire, duration);
  };

  // Freezes the countdown and the animation in place without resetting it — resuming
  // continues from exactly this remaining time, per spec.
  pauseTimerRun = () => {
    if (!this._running) return;
    this._running = false;
    clearTimeout(this._confirmTimer);
    this._confirmTimer = null;
    if (this._animation) this._animation.stop();
    const elapsed = Date.now() - this._runStartedAt;
    this._remainingMs = Math.max(0, this._remainingMs - elapsed);
  };

  // Like pause, but discards the remaining-time bookkeeping — used when the pending box
  // changes (fresh 4s window) or the card is torn down entirely.
  stopTimerRun = () => {
    this._running = false;
    clearTimeout(this._confirmTimer);
    this._confirmTimer = null;
    if (this._animation) this._animation.stop();
  };

  resetTimer = () => {
    this.stopTimerRun();
    this._remainingMs = HANDOFF_TIMER_DURATION_MS;
    this._fillAnim.setValue(1);
  };

  handleTimerExpire = () => {
    this._running = false;
    this._confirmTimer = null;
    // Must act EXACTLY as if the packer clicked the button — same prop, same path
    // (confirmBoxChampHandoff), per spec.
    if (this.props.onConfirm) this.props.onConfirm();
  };

  togglePause = () => {
    if (!this.timerEligible(this.props)) return;
    this.setState(prev => ({timerPaused: !prev.timerPaused}));
  };

  // QA hardening (GROOV-4994 hand-off timer): a manual click on the confirm button must
  // stop the internal countdown exactly like pause does, not just render nothing further
  // because the parent's kind flips to idle. Without this, a manual click while the
  // timer is still pending left `_confirmTimer` alive; only confirmBoxChampHandoff's own
  // _boxChampConfirmInFlight guard two components away happened to prevent an actual
  // double-fire in the live app, which is real but fragile — this component must be
  // correct on its own. Symmetric with pauseTimerRun/stopTimerRun: fully tears down both
  // the setTimeout and the Animated.timing before delegating to the same onConfirm prop
  // the timer's own expiry uses.
  handleManualConfirm = () => {
    this.stopTimerRun();
    if (this.props.onConfirm) this.props.onConfirm();
  };

  componentDidMount() {
    if (this.cardVisible(this.props)) this.focusInput();
    this._timerKey = this.timerKey(this.props);
    if (this.timerEligible(this.props)) {
      this._remainingMs = HANDOFF_TIMER_DURATION_MS;
      this._fillAnim.setValue(1);
      if (this.shouldTimerRun(this.props, this.state)) this.beginTimerRun();
    }
  }

  componentDidUpdate(prevProps) {
    const now = this.cardVisible(this.props);
    const before = this.cardVisible(prevProps);
    const key = props => `${props.state?.kind}:${props.state?.sealBox?.id ?? ''}:${props.state?.expectedBox?.id ?? ''}`;
    if (now && (!before || key(prevProps) !== key(this.props) || prevProps.message !== this.props.message)) this.focusInput();

    const newTimerKey = this.timerKey(this.props);
    if (newTimerKey !== this._timerKey) {
      this._timerKey = newTimerKey;
      this.resetTimer();
      if (this.state.timerPaused) {
        // A brand-new pending box always gets its own fresh, running countdown —
        // a pause on the box the packer just confirmed must not carry over.
        this.setState({timerPaused: false});
        return; // componentDidUpdate re-runs after this setState; the branch below
                // will then see the up-to-date shouldTimerRun and start the run.
      }
      if (this.shouldTimerRun(this.props, this.state)) this.beginTimerRun();
      return;
    }

    const runningNow = this.shouldTimerRun(this.props, this.state);
    if (runningNow && !this._running) {
      this.beginTimerRun();
    } else if (!runningNow && this._running) {
      this.pauseTimerRun();
    }
  }

  componentWillUnmount() {
    clearTimeout(this._focusTimer);
    this.stopTimerRun();
  }

  submitInput = event => {
    const submitted = event?.nativeEvent?.text;
    const value = (typeof submitted === 'string' ? submitted : this.state.input).trim();
    if (!value) return;
    this.setState({input: ''});
    if (this.props.onScan) this.props.onScan(value);
  };

  render() {
    const {
      state,
      onConfirm,
      setAsideItems,
      message,
      sealedContents,
      positionLabel,
      dismissed,
      onClose,
      onShow,
      onScan
    } = this.props;
    const kind = state && state.kind;
    if (!kind || kind === HANDOFF_IDLE) return null;

    const awaitingFirst = kind === HANDOFF_AWAITING_FIRST_VERIFY;
    const requiresScan = Boolean(state.requiresBoxScan);
    const timerEligible = this.timerEligible(this.props);
    const setAside = Array.isArray(setAsideItems) ? setAsideItems : [];
    const contents = Array.isArray(sealedContents) ? sealedContents : [];
    const nextBox = awaitingFirst ? state.expectedBox : state.nextBox;
    const heroBox = awaitingFirst ? state.expectedBox : state.sealBox;
    const unitCount = contents.reduce((sum, e) => sum + (Number(e.qty) || 0), 0);

    const title = awaitingFirst
      ? 'Start packing'
      : `Seal ${state.sealBox ? state.sealBox.name : 'this box'}`;

    let instruction;
    if (awaitingFirst) {
      instruction = requiresScan
        ? `Scan ${
            state.expectedBox ? state.expectedBox.name : 'the first box'
          } to start packing.`
        : `Press the button below to start packing${
            state.expectedBox ? ` ${state.expectedBox.name}` : ''
          }.`;
    } else {
      const sealName = state.sealBox ? state.sealBox.name : 'this box';
      const seal = `Seal ${sealName} and set it in line.`;
      if (requiresScan && state.expectedBox) {
        instruction = `${seal} Then scan ${state.expectedBox.name} to open it.`;
      } else if (state.isLastBox) {
        instruction = `${seal} That was the last box.`;
      } else {
        instruction = seal;
      }
    }

    const callToAction = requiresScan
      ? 'Scan or type the box barcode here, or use your normal scan field.'
      : awaitingFirst
      ? 'Press the button to start packing.'
      : state.isLastBox
      ? 'Scan the Next Box card in your normal scan field, or use the button to complete the order.'
      : 'Scan the Next Box card in your normal scan field, or use the button.';

    if (dismissed) {
      return (
        <View
          testID="boxchamp-handoff-banner"
          style={[noticeStyles.banner, {backgroundColor: noticeTones.warning.background}]}>
          <View style={{flexDirection: 'row', alignItems: 'center'}}>
            <Text
              testID="boxchamp-handoff-banner-text"
              style={[noticeStyles.text, {color: noticeTones.warning.text, flex: 1}]}>
              {message || instruction}
            </Text>
            {!!onShow && (
              <TouchableOpacity
                testID="boxchamp-handoff-show"
                onPress={onShow}
                style={{paddingHorizontal: 8, paddingVertical: 2}}>
                <Text style={[noticeStyles.text, {color: noticeTones.warning.text, textDecorationLine: 'underline'}]}>
                  Show
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      );
    }

    const {width, height} = Dimensions.get('window');
    const compact = width < 900 || height < 700;
    const topGap = compact ? 150 : 240;

    return (
      <View
        testID="boxchamp-handoff-overlay"
        pointerEvents="box-none"
        style={[
          modalStyles.overlay,
          {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            paddingTop: topGap,
            paddingBottom: 16,
            paddingHorizontal: 12,
          },
        ]}>
        <View
          testID="boxchamp-handoff-card"
          style={[
            modalStyles.dialog,
            {
              width: '100%',
              maxWidth: 620,
              maxHeight: Math.max(240, height - topGap - 24),
              flexShrink: 1,
            },
          ]}>
          <View style={modalStyles.header}>
            <Text style={modalStyles.headerTitle}>{title}</Text>
            {!!onClose && (
              <TouchableOpacity
                testID="boxchamp-handoff-close"
                onPress={onClose}
                style={{paddingHorizontal: 8, paddingVertical: 2}}>
                <Text style={[modalStyles.closeIcon, {color: tone.white, fontSize: 18}]}>✕</Text>
              </TouchableOpacity>
            )}
          </View>

          <ScrollView
            style={modalStyles.body}
            contentContainerStyle={{paddingBottom: 2}}>
            {!!heroBox && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: tone.hero,
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 14,
                }}>
                {!!heroBox.image && (
                  <Image
                    source={{uri: heroBox.image}}
                    style={thumb(46)}
                    resizeMode="contain"
                  />
                )}
                <View style={{flex: 1}}>
                  <Text style={label}>
                    {awaitingFirst ? 'FIRST BOX' : 'SEALING'}
                  </Text>
                  <Text
                    style={{
                      fontFamily: 'Poppins_600SemiBold',
                      fontSize: 18,
                      color: tone.white,
                      marginTop: 1,
                    }}>
                    {heroBox.name}
                  </Text>
                  {!!positionLabel && (
                    <Text
                      testID="boxchamp-handoff-position"
                      style={[label, {marginTop: 2}]}>
                      {positionLabel}
                    </Text>
                  )}
                </View>
              </View>
            )}

            <Text
              testID="boxchamp-handoff-instruction"
              style={{
                fontFamily: 'Poppins_500Medium',
                fontSize: 14,
                color: tone.body,
                marginBottom: requiresScan && !!onScan && contents.length === 0 && setAside.length === 0 ? 6 : 14,
              }}>
              {instruction}
            </Text>

            {contents.length > 0 && (
              <View testID="boxchamp-handoff-contents" style={{marginBottom: 14}}>
                <Text style={[label, {marginBottom: 7}]}>
                  CHECK BEFORE SEALING · {unitCount}{' '}
                  {unitCount === 1 ? 'ITEM' : 'ITEMS'}
                </Text>
                <View>
                  {contents.map(entry => (
                    <View
                      key={`sealed-${entry.key}`}
                      testID={`boxchamp-handoff-content-${entry.key}`}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        borderWidth: 1,
                        borderColor: tone.rule,
                        borderRadius: 6,
                        paddingVertical: 8,
                        paddingHorizontal: 10,
                        marginBottom: 6,
                      }}>
                      {!!entry.image && (
                        <Image
                          source={{uri: entry.image}}
                          style={thumb(34)}
                          resizeMode="contain"
                        />
                      )}
                      <View style={{flex: 1}}>
                        <Text
                          style={{
                            fontFamily: 'Poppins_500Medium',
                            fontSize: 14,
                            color: tone.white,
                          }}>
                          {entry.name}
                        </Text>
                        {!!entry.sku && (
                          <Text
                            style={{
                              fontFamily: 'Poppins_500Medium',
                              fontSize: 11.5,
                              color: tone.muted,
                            }}>
                            {entry.sku}
                          </Text>
                        )}
                      </View>
                      <Text
                        style={{
                          fontFamily: 'Poppins_600SemiBold',
                          fontSize: 16,
                          color: tone.accent,
                        }}>
                        × {entry.qty}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {setAside.length > 0 && (
              <View
                testID="boxchamp-handoff-set-aside"
                style={{
                  backgroundColor: tone.warnBg,
                  borderLeftWidth: 3,
                  borderLeftColor: tone.warn,
                  borderRadius: 6,
                  paddingVertical: 8,
                  paddingHorizontal: 10,
                  marginBottom: 14,
                }}>
                <Text style={[label, {color: tone.warn, marginBottom: 2}]}>
                  SET ASIDE FOR THIS BOX
                </Text>
                {setAside.map((item, index) => (
                  <Text
                    key={`set-aside-${item.key || item.order_item_id || index}`}
                    style={{
                      fontFamily: 'Poppins_500Medium',
                      fontSize: 13.5,
                      color: tone.body,
                    }}>
                    {item.name} × {item.qty}
                  </Text>
                ))}
              </View>
            )}

            {!!nextBox && !awaitingFirst && (
              <View testID="boxchamp-handoff-next-box">
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginBottom: 7,
                  }}>
                  <Text style={label}>NEXT</Text>
                  <View
                    style={{
                      flex: 1,
                      height: 1,
                      backgroundColor: tone.rule,
                      marginLeft: 10,
                    }}
                  />
                </View>
                <View style={{flexDirection: 'row', alignItems: 'center'}}>
                  {!!nextBox.image && (
                    <Image
                      testID="boxchamp-handoff-next-image"
                      source={{uri: nextBox.image}}
                      style={thumb(30)}
                      resizeMode="contain"
                    />
                  )}
                  <Text
                    style={{
                      fontFamily: 'Poppins_500Medium',
                      fontSize: 14,
                      color: tone.body,
                    }}>
                    {nextBox.name}
                  </Text>
                </View>
              </View>
            )}

            {!!message && (
              <Text
                testID="boxchamp-handoff-message"
                style={{
                  fontFamily: 'Poppins_500Medium',
                  fontSize: 13.5,
                  color: tone.warn,
                  marginTop: 12,
                }}>
                {message}
              </Text>
            )}
            {requiresScan && !!onScan && (
            <View style={{marginTop: 4}}>
              <TextInput
                ref={this.inputRef}
                testID="boxchamp-handoff-input"
                autoFocus
                blurOnSubmit={false}
                value={this.state.input}
                onChangeText={input => this.setState({input})}
                onSubmitEditing={this.submitInput}
                placeholder={`Scan ${nextBox ? nextBox.name : 'the box'}`}
                placeholderTextColor={tone.muted}
                style={{
                  backgroundColor: tone.white,
                  borderRadius: 6,
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                  fontFamily: 'Poppins_500Medium',
                  fontSize: 15,
                  color: '#1b2a3a'
                }}
              />
            </View>
          )}
          </ScrollView>

          <View style={modalStyles.footer}>
            <Text
              testID="boxchamp-handoff-hint"
              style={{
                fontFamily: 'Poppins_500Medium',
                fontSize: 12.5,
                color: tone.muted,
                flex: 1,
              }}>
              {callToAction}
            </Text>
            {!requiresScan && (
              <View
                testID="boxchamp-next-box-wrap"
                style={[
                  modalStyles.queueButton,
                  {
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    paddingVertical: 0,
                    paddingHorizontal: 0,
                    overflow: 'hidden',
                    // The wrap's own background is the "drained" color once the fill
                    // has shrunk away; when there is no timer (button always fully
                    // "full") it stays the ordinary solid green from queueButton.
                    backgroundColor: timerEligible ? tone.rule : modalStyles.queueButton.backgroundColor,
                  },
                ]}>
                {timerEligible && (
                  // The countdown fill: full green at the left edge, shrinking (right
                  // edge moving left) toward empty over HANDOFF_TIMER_DURATION_MS.
                  // Sits behind the pause icon and label, which is why they're drawn
                  // after it here and this layer is pointerEvents="none".
                  <Animated.View
                    testID="boxchamp-next-box-fill"
                    pointerEvents="none"
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: 0,
                      backgroundColor: '#1CBB77',
                      width: this._fillAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['0%', '100%'],
                      }),
                    }}
                  />
                )}
                {timerEligible && (
                  <TouchableOpacity
                    testID="boxchamp-next-box-pause"
                    onPress={this.togglePause}
                    hitSlop={{top: 8, bottom: 8, left: 8, right: 4}}
                    style={{
                      paddingVertical: 10,
                      paddingLeft: 14,
                      paddingRight: 6,
                      justifyContent: 'center',
                    }}>
                    <Text style={{color: tone.white, fontSize: 13}}>
                      {this.state.timerPaused ? '▶' : '❚❚'}
                    </Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  testID="boxchamp-next-box-button"
                  onPress={this.handleManualConfirm}
                  style={{
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: 10,
                    paddingLeft: timerEligible ? 6 : 20,
                    paddingRight: 20,
                  }}>
                  <Text style={modalStyles.queueButtonText}>
                    {awaitingFirst
                      ? 'Start packing'
                      : state.isLastBox
                      ? 'Complete Order'
                      : 'Next Box'}
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
      </View>
    );
  }
}
